import { createContext, useContext, type Dispatch, type SetStateAction } from 'react';

export interface AppConversation {
  conversation_sid: string;
  friendly_name?: string;
  last_message?: string;
  participant_identity: string;
  participant_avatar?: string;
  slug?: string;
  hasUnreadMessages?: boolean;
  last_message_time?: string;
  date_created?: string;
  typing?: boolean;
  unreadMessagesCount?: number;
}

export interface AppMessage {
  message_sid?: string;
  conversation_sid: string;
  message_body?: string;
  media_url?: string;
  timestamp: string;
  sender_identity: string;
  author?: string;
  author_name?: string;
  body?: string;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  retry?: () => void;
  attributes?: Record<string, unknown>;
  temp_id?: string;
}

export interface TwilioConversationsContextType {
  conversations: AppConversation[];
  messages: { [sid: string]: AppMessage[] };
  typing: { [sid: string]: boolean };
  loading: boolean;
  error: string | null;
  twilioStatus: 'connecting' | 'connected' | 'failed' | 'denied';
  fetchMessages: (conversationSid: string) => Promise<AppMessage[]>;
  sendMessage: (
    conversationSid: string,
    body: string,
    mediaUrl?: string,
    tempId?: string
  ) => Promise<{ success: boolean; message?: AppMessage; error?: string }>;
  sendTypingIndicator: (conversationSid: string) => void;
  createConversation: (
    participantIdentity: string,
    firstMessage: string
  ) => Promise<{ success: boolean; conversationSid?: string; error?: string }>;
  markConversationAsRead: (conversationSid: string) => Promise<void>;
  reconnect: () => void;
  refreshUnreadMessagesCount: () => void;
  refreshConversations: () => Promise<void>;
}

export const TwilioConversationsContext = createContext<TwilioConversationsContextType | undefined>(
  undefined
);

export const EMPTY_TWILIO_CONTEXT: TwilioConversationsContextType = {
  conversations: [],
  messages: {},
  typing: {},
  loading: false,
  error: null,
  twilioStatus: 'connecting',
  fetchMessages: async () => [],
  sendMessage: async () => ({ success: false, error: 'Provider not available' }),
  sendTypingIndicator: () => {},
  createConversation: async () => ({ success: false, error: 'Provider not available' }),
  markConversationAsRead: async () => {},
  reconnect: () => {},
  refreshUnreadMessagesCount: () => {},
  refreshConversations: async () => {},
};

export const useTwilioConversations = () => {
  const ctx = useContext(TwilioConversationsContext);
  if (!ctx) {
    throw new Error('useTwilioConversations doit être utilisé dans un TwilioConversationsProvider');
  }
  return ctx;
};

export const useSafeTwilioConversations = () => {
  return useContext(TwilioConversationsContext) ?? EMPTY_TWILIO_CONTEXT;
};

/** Alias explicite : tolère l’absence du provider lazy (badges / menu). */
export const useOptionalTwilioConversations = useSafeTwilioConversations;

/** État initial tant que le chunk Twilio (@twilio/conversations) n’est pas chargé. */
export function createConnectingTwilioContext(): TwilioConversationsContextType {
  return {
    ...EMPTY_TWILIO_CONTEXT,
    loading: true,
    twilioStatus: 'connecting',
  };
}

/** Pont shell → runtime lazy (publie la valeur du provider lourd sans remonter l’app). */
export const TwilioRuntimeBridgeContext = createContext<
  Dispatch<SetStateAction<TwilioConversationsContextType>> | null
>(null);
